
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Challenge } from '../types/game';
import { useGame } from '../contexts/GameContext';
import { AlertTriangle, Check, ArrowRight, Trophy, ChartBar, Award } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { motion, AnimatePresence } from "framer-motion";

interface DecisionModalProps {
  challenge: Challenge;
  onClose: () => void;
}

const getDecisionTypeLabel = (careerLevel: string): string => {
  switch (careerLevel) {
    case 'Senior':
      return 'Strategic Decision Required';
    case 'Mid-Level':
      return 'Technical Decision Required';
    default:
      return 'Action Required';
  }
};

const DecisionModal: React.FC<DecisionModalProps> = ({ challenge, onClose }) => {
  const { makeDecision, updateKpi, completeChallenge, addSkill, careerLevel } = useGame();
  const [selectedOption, setSelectedOption] = useState<typeof challenge.options[0] | null>(null);
  const [showOutcome, setShowOutcome] = useState(false);
  
  const decisionTypeLabel = getDecisionTypeLabel(careerLevel);
  
  const getImpactColor = (value: number): string => {
    if (value > 0) return "text-bitcoin-accent";
    if (value < 0) return "text-destructive";
    return "text-muted-foreground";
  };
  
  const handleDecision = (option: typeof challenge.options[0]) => {
    setSelectedOption(option);
    
    // Apply KPI effects with animation
    Object.entries(option.kpiEffects).forEach(([kpi, value]) => {
      updateKpi(kpi, value);
      
      if (Math.abs(value) >= 2 || (kpi === 'energyCost' && Math.abs(value) >= 0.01)) {
        const direction = value > 0 ? 'increased' : 'decreased';
        const absValue = Math.abs(value);
        
        toast(`${kpi} ${direction}!`, {
          description: `Your ${kpi} has ${direction} by ${absValue}${kpi === 'energyCost' ? '/kWh' : '%'}`,
          duration: 3000,
        });
      }
    });
    
    // Show experience gained with animation
    toast("Experience Gained!", {
      description: `You've gained ${option.experienceGain} experience points.`,
      icon: <Check className="h-4 w-4" />,
      duration: 3000,
    });
    
    makeDecision(option.text, option.outcome);
    
    if (option.skillUnlock) {
      addSkill(option.skillUnlock);
      toast("New Skill Unlocked!", {
        description: `You've gained the ${option.skillUnlock.name} skill!`,
        icon: <Award className="h-4 w-4" />,
        duration: 4000,
      });
    }
    
    // Show outcome with animation
    setTimeout(() => {
      setShowOutcome(true);
    }, 500);
    
    // Don't close immediately after selection to show the outcome
    setTimeout(() => {
      completeChallenge();
      onClose();
    }, 4000); // Increased from 3500 to 4000ms to give users more time to read
  };
  
  const getChallengeIcon = () => {
    switch (challenge.difficulty) {
      case 5:
        return <Trophy className="mr-2 text-bitcoin-primary h-5 w-5" />;
      case 4:
        return <Award className="mr-2 text-bitcoin-primary h-5 w-5" />;
      default:
        return <AlertTriangle className="mr-2 text-bitcoin-primary h-5 w-5" />;
    }
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[550px] p-0 max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
        <div className="bg-bitcoin-dark text-white p-4 flex-shrink-0">
          <DialogHeader>
            <div className="text-xs font-medium text-bitcoin-primary mb-2">
              {decisionTypeLabel}
            </div>
            <DialogTitle className="text-xl flex items-center">
              {getChallengeIcon()} {challenge.title}
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-base">
              Difficulty: {Array(challenge.difficulty).fill('★').join('')}
            </DialogDescription>
          </DialogHeader>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          <p className="mb-6">{challenge.description}</p>
          
          <div className="space-y-3">
            <AnimatePresence>
              {challenge.options.map((option, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border rounded-md p-4 hover:border-bitcoin-primary cursor-pointer transition-all ${
                    selectedOption === option 
                      ? 'border-bitcoin-primary bg-bitcoin-primary/5 shadow-md' 
                      : 'hover:shadow-md hover:translate-y-[-2px]'
                  }`}
                  onClick={() => !selectedOption && handleDecision(option)}
                >
                  <h3 className="font-medium mb-2">{option.text}</h3>
                  {selectedOption ? (
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-2 animate-fade-in">
                      {Object.entries(option.kpiEffects).map(([kpi, value]) => (
                        <div key={kpi} className="flex items-center">
                          <span className={getImpactColor(value)}>
                            {value > 0 ? "+" : ""}{value}
                          </span>
                          <span className="ml-1">{kpi}</span>
                        </div>
                      ))}
                      <div className="flex items-center text-bitcoin-accent">
                        +{option.experienceGain} XP
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Select to see impact</div>
                  )}
                  {selectedOption === option && option.skillUnlock && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-xs bg-bitcoin-primary/10 text-bitcoin-primary px-2 py-1 rounded inline-block"
                    >
                      <div className="flex items-center">
                        <ChartBar className="h-3 w-3 mr-1" /> 
                        Unlocks: {option.skillUnlock.name}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          <AnimatePresence>
            {showOutcome && selectedOption && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-6 p-4 border border-web3-green/20 bg-web3-green/10 rounded-md"
              >
                <h3 className="font-medium mb-2 text-web3-green flex items-center">
                  <Check className="mr-2 h-5 w-5" /> Outcome
                </h3>
                <p>{selectedOption.outcome}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <DialogFooter className="bg-slate-50 px-6 py-4 flex-shrink-0">
          <p className="text-xs text-muted-foreground flex items-center">
            {selectedOption 
              ? <><ArrowRight className="h-4 w-4 mr-1" /> Impact shown above. Dialog will close automatically...</>
              : 'Choose carefully. Your decision will affect your career trajectory.'}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DecisionModal;
